import { createContext, ReactNode, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useClientPortalConfigContext } from "./ClientPortalConfig.ctx";
import { AccountModel } from "types/generated/models";
import { useNavigate, useLocation } from "react-router";
import { ROUTES } from "../routes";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import { useClientPortalDispatch, clearBookingState } from "../redux";

interface IAuthorizationContext {
    loggedIn: boolean;
    userDetails: AccountModel | undefined;
    logoutUser: () => Promise<void>;
    loginUser: (accountModel: AccountModel) => void;
}

const AuthorizationContext = createContext<IAuthorizationContext>({
    loggedIn: false,
    userDetails: undefined,
    logoutUser: async () => {},
    loginUser: () => {},
});
AuthorizationContext.displayName = "AuthorizationContext";

export function AuthorizationContextProvider({ children }: { children: ReactNode }) {
    const { clientPortalConfig } = useClientPortalConfigContext();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const dispatch = useClientPortalDispatch();
    const shouldInvalidateQueriesRef = useRef(false);

    // Derive loggedIn and userDetails state from clientPortalConfig
    const loggedIn = clientPortalConfig?.userLoggedIn ?? false;
    const userDetails = clientPortalConfig?.userDetails ?? undefined;

    // Watch for location changes and invalidate queries if needed
    useEffect(() => {
        if (shouldInvalidateQueriesRef.current) {
            shouldInvalidateQueriesRef.current = false;
            void queryClient.invalidateQueries({ refetchType: "active" });
        }
    }, [location, queryClient]);

    const logoutUser = useCallback(async () => {
        dispatch(clearBookingState("all"));
        // Cancel all in-flight queries to prevent refetch attempts during logout
        await queryClient.cancelQueries();
        // Mark that we should invalidate queries after navigation completes
        shouldInvalidateQueriesRef.current = true;
        // Navigate to home page - the useEffect will invalidate queries after navigation
        void navigate(ROUTES.HOME.urlPath);
    }, [dispatch, navigate, queryClient]);

    const loginUser = useCallback(
        (accountModel: AccountModel) => {
            const bookingRoutes = [ROUTES.APPOINTMENT_BOOKING.urlPath, ROUTES.CLASS_BOOKING.urlPath];
            const isBookingRoute =
                accountModel.returnUrl && bookingRoutes.some((route) => accountModel.returnUrl.includes(route));
            const isAllowedToBook = accountModel.isLoggedInUserAllowedToBookAppointment ?? true;

            if (isBookingRoute && !isAllowedToBook) {
                enqueueSnackbar(
                    "Your profile is not currently set up to book appointments. Please contact the practice for assistance.",
                    { variant: "error" }
                );
                void navigate(ROUTES.HOME.urlPath);
            } else if (accountModel?.returnUrl) {
                void navigate(accountModel.returnUrl);
            }

            void queryClient.invalidateQueries({ refetchType: "active" });
        },
        [navigate, queryClient]
    );

    const contextData = useMemo(
        () => ({ loggedIn, userDetails, logoutUser, loginUser }),
        [loggedIn, userDetails, logoutUser, loginUser]
    );

    return <AuthorizationContext.Provider value={contextData}>{children}</AuthorizationContext.Provider>;
}

export function useAuthorizationContext() {
    return useContext(AuthorizationContext);
}
